import { PageHeader, LoadingState, DataTable, EmptyState, ActionToolbar } from '../components/AdminPageUI';
import SummaryStrip, { SummaryMetric } from '../components/SummaryStrip';
// AdminResetRequestsPage.jsx
import React, { useEffect, useState } from "react";
import { Box, Typography, Card, CardContent, Table, TableHead, TableBody, TableRow, TableCell, CircularProgress, Button, Chip, Stack, Tooltip, IconButton, Alert, Dialog, DialogTitle, DialogContent, DialogActions, TextField, InputAdornment, Container, Avatar, Grid, FormControl, InputLabel, MenuItem, Select, Collapse, Divider, Paper } from "@mui/material";
import { CheckCircle, Cancel, Refresh, Visibility, VisibilityOff, Info as InfoIcon, LockReset, Person, Email, Schedule, AdminPanelSettings } from "@mui/icons-material";
import { alpha, useTheme } from "@mui/material/styles";
import axios from "axios";

const API_BASE_URL = "https://my-school-backend-z51g.onrender.com/api";

const statusColors = {
  pending: "warning",
  approved: "success",
  rejected: "error",
};

const statusIcons = {
  pending: <Schedule color="warning" />,
  approved: <CheckCircle color="success" />,
  rejected: <Cancel color="error" />,
};

export default function AdminResetRequestsPage() {
  const theme = useTheme();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  // Modal state
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [note, setNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Stats state
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0
  });

  const fetchRequests = async () => {
    setLoading(true);
    setMessage("");
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        setMessage("Please log in to view requests.");
        setLoading(false);
        return;
      }

      const res = await axios.get(`${API_BASE_URL}/auth/admin/reset-requests`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const requestsData = res.data.requests || [];
      setRequests(requestsData);

      // Calculate stats
      setStats({
        total: requestsData.length,
        pending: requestsData.filter(req => req.status === 'pending').length,
        approved: requestsData.filter(req => req.status === 'approved').length,
        rejected: requestsData.filter(req => req.status === 'rejected').length
      });
    } catch (err) {
      if (err.response?.status === 401) {
        // Session expired
        // console.warn("Session expired or invalid token.");
        setMessage("Session expired. Please log out and log in again.");
        // Optional: Redirect or clear token could be handled here if we had access to navigate/auth context
      } else {
        console.error("❌ Failed to fetch reset requests:", err.response?.data || err.message);
        setMessage("Failed to load reset requests. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  // Open approve modal (instead of immediate approve)
  const openApproveModal = (req) => {
    setSelectedRequest(req);
    setNewPassword("");
    setConfirmPassword("");
    setNote("");
    setShowPassword(false);
    setApproveModalOpen(true);
    setMessage("");
  };

  const closeApproveModal = () => {
    setApproveModalOpen(false);
    setSelectedRequest(null);
    setNewPassword("");
    setConfirmPassword("");
    setNote("");
    setIsSubmitting(false);
  };

  const submitApprove = async () => {
    if (!newPassword || newPassword.length < 6) {
      setMessage("❌ New password must be at least 6 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setMessage("❌ New password and confirmation do not match.");
      return;
    }
    if (!selectedRequest) {
      setMessage("❌ No request selected.");
      return;
    }

    try {
      setIsSubmitting(true);
      const token = localStorage.getItem("token");
      const res = await axios.put(
        `${API_BASE_URL}/auth/admin/reset-requests/${selectedRequest._id}/approve`,
        { newPassword, note },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log("✅ Approved:", res.data);
      setMessage(`✅ ${res.data.message || "Request approved successfully"}`);
      closeApproveModal();
      fetchRequests();
    } catch (err) {
      console.error("❌ Failed to approve request:", err.response?.data || err.message);
      const errMsg = err.response?.data?.message || "Failed to approve request. Try again.";
      setMessage(`❌ ${errMsg}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async (id) => {
    try {
      setRefreshing(true);
      const token = localStorage.getItem("token");
      const res = await axios.put(
        `${API_BASE_URL}/auth/admin/reset-requests/${id}/reject`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log("🚫 Rejected:", res.data);
      setMessage(`🚫 ${res.data.message || "Request rejected successfully"}`);
      fetchRequests();
    } catch (err) {
      console.error("❌ Failed to reject request:", err.response?.data || err.message);
      setMessage("Failed to reject request. Try again.");
    } finally {
      setRefreshing(false);
    }
  };

  const getInitials = (email) => {
    return email ? email.charAt(0).toUpperCase() : 'U';
  };

  const getRoleColor = (role) => {
    const colors = {
      student: 'primary',
      teacher: 'secondary',
      parent: 'info',
      admin: 'warning'
    };
    return colors[role] || 'default';
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <PageHeader title={<>Password Reset Management</>} description={<>Review and manage password reset requests from users</>}></PageHeader>
      <SummaryStrip container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
          <SummaryMetric sx={{
            boxShadow: 3,
            border: `1px solid ${theme.palette.primary.main}`,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: 6
            }
          }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                  <LockReset />
                </Avatar>
                <Box>
                  <Typography variant="h6" color="text.secondary">
                    Total Requests
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    All reset requests
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </SummaryMetric>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
          <SummaryMetric sx={{
            boxShadow: 3,
            border: `1px solid ${theme.palette.warning.main}`,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: 6
            }
          }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: theme.palette.warning.main }}>
                  <Schedule />
                </Avatar>
                <Box>
                  <Typography variant="h6" color="text.secondary">
                    Pending
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.pending}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Awaiting review
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </SummaryMetric>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
          <SummaryMetric sx={{
            boxShadow: 3,
            border: `1px solid ${theme.palette.success.main}`,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: 6
            }
          }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: theme.palette.success.main }}>
                  <CheckCircle />
                </Avatar>
                <Box>
                  <Typography variant="h6" color="text.secondary">
                    Approved
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.approved}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completed requests
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </SummaryMetric>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
          <SummaryMetric sx={{
            boxShadow: 3,
            border: `1px solid ${theme.palette.error.main}`,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: 6
            }
          }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: theme.palette.error.main }}>
                  <Cancel />
                </Avatar>
                <Box>
                  <Typography variant="h6" color="text.secondary">
                    Rejected
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.rejected}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Denied requests
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </SummaryMetric>
        </Grid>
      </SummaryStrip>

      {/* Stats Cards - Updated Grid v2 */}
      

      {message && (
        <Alert
          severity={
            message.startsWith("✅")
              ? "success"
              : message.startsWith("🚫")
                ? "warning"
                : message.startsWith("❌")
                  ? "error"
                  : "info"
          }
          sx={{ mb: 3, borderRadius: 2 }}
        >
          {message}
        </Alert>
      )}

      {/* Requests Table Card */}
      <Card sx={{ boxShadow: 3, border: `1px solid ${theme.palette.divider}` }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" fontWeight="600">
                Reset Requests ({requests.length})
              </Typography>
              {requests.length > 0 && (
                <Stack direction="row" spacing={2} alignItems="center">
                  <Chip
                    label={`${stats.total} total`}
                    color="primary"
                    variant="outlined"
                  />
                  <Chip
                    label={`${stats.pending} pending`}
                    color="warning"
                    variant="outlined"
                  />
                  <Tooltip title="Refresh">
                    <IconButton
                      onClick={fetchRequests}
                      disabled={refreshing}
                      sx={{
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        },
                      }}
                    >
                      <Refresh />
                    </IconButton>
                  </Tooltip>
                </Stack>
              )}
            </Stack>
          </Box>

          {loading ? (
            <LoadingState />
          ) : requests.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <LockReset sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.5, mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No password reset requests
              </Typography>
              <Typography variant="body2" color="text.secondary">
                All reset requests will appear here for review
              </Typography>
            </Box>
          ) : (
            <DataTable>
              <Table>
                <TableHead sx={{ bgcolor: theme.palette.background.default }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }}>USER</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }}>ROLE</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }}>REQUESTED AT</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }}>STATUS</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2, textAlign: 'center' }}>ACTIONS</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
{requests.length === 0 && <TableRow><TableCell colSpan={100}><EmptyState title="No records found" description="Records will appear here when available. Check your selected filters." /></TableCell></TableRow>}

                  {requests.map((req) => (
                    <TableRow
                      key={req._id}
                      sx={{
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.02)
                        }
                      }}
                    >
                      <TableCell sx={{ py: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 40, height: 40 }}>
                            {getInitials(req.email)}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle1" fontWeight="500">
                              {req.email}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Password reset request
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Chip
                          label={req.role}
                          color={getRoleColor(req.role)}
                          variant="outlined"
                          size="small"
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Stack spacing={0.5}>
                          <Typography variant="body2" fontWeight="500">
                            {new Date(req.requestedAt || req.createdAt || req.createdAt).toLocaleDateString("en-US", {
                              dateStyle: "medium",
                            })}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {new Date(req.requestedAt || req.createdAt || req.createdAt).toLocaleTimeString("en-US", {
                              timeStyle: "short",
                            })}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {statusIcons[req.status]}
                          <Chip
                            label={req.status}
                            color={statusColors[req.status] || "default"}
                            variant="filled"
                            size="small"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ py: 2, textAlign: 'center' }}>
                        {req.status === "pending" ? (
                          <ActionToolbar direction="row" spacing={1} justifyContent="center">
                            <Tooltip title="Approve Request">
                              <Button
                                variant="contained"
                                color="success"
                                size="small"
                                startIcon={<CheckCircle />}
                                onClick={() => openApproveModal(req)}
                                disabled={refreshing}
                                sx={{
                                  minWidth: 'auto',
                                  px: 2,
                                  borderRadius: 1,
                                }}
                              >
                                Approve
                              </Button>
                            </Tooltip>

                            <Tooltip title="Reject Request">
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                startIcon={<Cancel />}
                                onClick={() => handleReject(req._id)}
                                disabled={refreshing}
                                sx={{
                                  minWidth: 'auto',
                                  px: 2,
                                  borderRadius: 1,
                                }}
                              >
                                Reject
                              </Button>
                            </Tooltip>

                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setMessage(
                                    `ℹ️ Request by ${req.email} (${req.role}) — requested at ${new Date(
                                      req.requestedAt || req.createdAt || req.createdAt
                                    ).toLocaleString()}`
                                  );
                                }}
                                sx={{
                                  minWidth: 32,
                                  minHeight: 32,
                                  borderRadius: 1,
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.info.main, 0.1),
                                  },
                                }}
                              >
                                <InfoIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </ActionToolbar>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {req.status === "approved"
                              ? "✅ Approved"
                              : req.status === "rejected"
                                ? "❌ Rejected"
                                : "—"}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </DataTable>
          )}
        </CardContent>
      </Card>

      {/* Approve Modal */}
      <Dialog
        open={approveModalOpen}
        onClose={closeApproveModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{
          bgcolor: theme.palette.success.main,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <CheckCircle />
          Approve Reset Request
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Set a new password for the user. This will immediately overwrite their current password.
          </Typography>

          {selectedRequest ? (
            <Box sx={{ display: "grid", gap: 3 }}>
              {/* User Info */}
              <Paper variant="outlined" sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                    {getInitials(selectedRequest.email)}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="500">
                      {selectedRequest.email}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                      {selectedRequest.role} • Requested {new Date(selectedRequest.requestedAt || selectedRequest.createdAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              <TextField
                label="New Password"
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                fullWidth
                error={newPassword && newPassword.length < 6}
                helperText={newPassword && newPassword.length < 6 ? "Minimum 6 characters required" : "Set a secure password for the user"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((s) => !s)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Confirm New Password"
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                fullWidth
                error={confirmPassword && newPassword !== confirmPassword}
                helperText={confirmPassword && newPassword !== confirmPassword ? "Passwords do not match" : "Re-enter the new password"}
              />

              <TextField
                label="Admin Note (Optional)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                placeholder="Add any notes about this reset approval..."
                helperText="This note will be recorded with the request"
              />
            </Box>
          ) : (
            <Typography>No request selected.</Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={closeApproveModal}
            variant="outlined"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={submitApprove}
            disabled={isSubmitting || !newPassword || !confirmPassword || newPassword !== confirmPassword || newPassword.length < 6}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : <CheckCircle />}
          >
            {isSubmitting ? "Processing..." : "Approve & Set Password"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}